import { Button, ConfigProvider, Flex, Form, Modal, Typography } from "antd";
import type { ButtonProps, FormInstance } from "antd";
import type { ReactNode } from "react";
import { translate } from "shared/languages";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "./ModalChrome";

/**
 * A confirm button that stays disabled until a choice in the dialog's form
 * has a value. A static Modal renders its footer once, so the button watches
 * the form itself (Form.useWatch) rather than reading it at open time.
 */
export const RequiredChoiceButton = ({
  choiceForm,
  choiceName,
  requiredWhen,
  ...buttonProps
}: ButtonProps & {
  choiceForm: FormInstance;
  choiceName: string;
  /**
   * A second field the choice itself can make mandatory: it is watched too,
   * and must hold non-blank text whenever the chosen value is `choiceIs`
   * (a void reason of "other" needs the note that says what happened).
   */
  requiredWhen?: { choiceIs: unknown; field: string };
}) => {
  const value = Form.useWatch(choiceName, choiceForm);
  // Watched unconditionally (hooks cannot be conditional); without a
  // requiredWhen this just re-reads the choice, which costs nothing.
  const dependent = Form.useWatch(requiredWhen?.field ?? choiceName, choiceForm);
  const missingChoice = value === undefined || value === null;
  const missingDependent =
    !!requiredWhen &&
    value === requiredWhen.choiceIs &&
    !String(dependent ?? "").trim();
  return (
    <Button {...buttonProps} disabled={missingChoice || missingDependent} />
  );
};

/**
 * Generic confirm dialog in the shared popup chrome (2026-06-09 prototype):
 * single display-font title with an optional alert dot, a muted explainer (or
 * arbitrary body), and the popup button-color system — solid-ink Batal dismiss,
 * navy confirm, red confirm for destructive actions. One source so every
 * schedule confirm (reorder, remove, cancel booking, undo) stays consistent
 * instead of each restyling its own AntD Modal.confirm.
 */
export const showConfirmModal = ({
  tone,
  title,
  content,
  confirmLabel,
  destructive = false,
  width = 400,
  top,
  requireChoice,
  onConfirm,
  onDismiss,
}: {
  // Alert dot beside the title: error/amber for destructive or warning
  // confirms, omitted for neutral ones.
  tone?: "error" | "amber" | "success";
  title: string;
  // A string renders as the muted explainer; pass JSX for richer bodies
  // (e.g. a form with a select).
  content: ReactNode;
  confirmLabel: string;
  // Renders the confirm button red (token danger) for irreversible actions.
  destructive?: boolean;
  width?: number;
  // Vertical offset of the dialog (CSS top). Opt-in so existing confirms keep
  // AntD's default 100px-from-top; pass a vh value to scale with the screen.
  top?: number | string;
  // Keeps the confirm button disabled until this field of the form rendered
  // in `content` has a value (e.g. the cancel type).
  requireChoice?: { form: FormInstance; name: string };
  onConfirm: () => void;
  onDismiss?: () => void;
}) => {
  const dismiss = () => {
    onDismiss?.();
    Modal.destroyAll();
  };
  Modal.confirm({
    icon: null,
    width,
    ...(top !== undefined ? { style: { top } } : {}),
    title: <ModalHeader tone={tone} title={title} />,
    content:
      typeof content === "string" ? (
        <Typography.Text style={modalSubtitleStyle}>{content}</Typography.Text>
      ) : (
        content
      ),
    closable: true,
    // AntD's static dialogs (Modal.confirm/warning/success/info/error) ignore
    // a click outside them by default, while every component Modal in the
    // app closes on one. Every static dialog opts in, so the two kinds
    // behave the same; the click runs onCancel, the same path as Batal.
    maskClosable: true,
    footer: (
      <ConfigProvider theme={modalButtonsTheme}>
        <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
          <Button onClick={dismiss}>{translate("button.cancel")}</Button>
          {requireChoice ? (
            <RequiredChoiceButton
              choiceForm={requireChoice.form}
              choiceName={requireChoice.name}
              type="primary"
              danger={destructive}
              onClick={() => {
                onConfirm();
                Modal.destroyAll();
              }}
            >
              {confirmLabel}
            </RequiredChoiceButton>
          ) : (
            <Button
              type="primary"
              danger={destructive}
              onClick={() => {
                onConfirm();
                Modal.destroyAll();
              }}
            >
              {confirmLabel}
            </Button>
          )}
        </Flex>
      </ConfigProvider>
    ),
    onCancel: dismiss,
  });
};
